import { Injectable, signal, computed } from '@angular/core';

/**
 * SharedAuthStateService - Breaks circular dependency between AuthStore and TenantStore
 *
 * ARCH-002: This service provides shared authentication state that can be consumed by both
 * AuthStore and TenantStore without creating circular dependencies.
 *
 * ## Architecture Decision
 *
 * The Problem:
 * - TenantStore needs to check authentication state (isAuthenticated, isLoggedIn)
 * - If TenantStore imports AuthStore, and AuthStore ever needs tenant info, we get:
 *   TenantStore -> AuthStore -> TenantStore (circular dependency)
 *
 * The Solution:
 * - Extract shared auth state into this minimal, dependency-free service
 * - AuthService writes to this service when auth state changes
 * - TenantStore reads from this service instead of AuthStore
 * - AuthStore can also sync with this service for consistency
 *
 * ## Data Flow
 *
 * ```
 *                    +-----------------------+
 *                    | SharedAuthStateService |
 *                    |  (signals: writable)   |
 *                    +-----------+-----------+
 *                               /|\
 *            WRITES              |              READS
 *              |                 |                |
 *   +----------+---------+       |      +---------+--------+
 *   |     AuthService    |       |      |    TenantStore   |
 *   | (login/logout/etc) |       |      | (canSwitchTenant)|
 *   +--------------------+       |      +------------------+
 *                                |
 *                      +---------+--------+
 *                      |    AuthStore     |
 *                      | (sync via effect)|
 *                      +------------------+
 * ```
 *
 * ## Usage
 *
 * In AuthService (writes):
 * ```typescript
 * this.sharedAuthState.setAuthenticated(true);
 * this.sharedAuthState.setCurrentUserId(user.id);
 * this.sharedAuthState.setCurrentTenantId(user.tenantId);
 * ```
 *
 * In TenantStore (reads):
 * ```typescript
 * readonly canSwitchTenant = computed(() =>
 *   this.hasMultipleTenants() && !this.isLoadingSignal() && this.sharedAuthState.isAuthenticated()
 * );
 * ```
 *
 * @see https://angular.io/errors/NG0200 for circular dependency detection
 */
@Injectable({
  providedIn: 'root'
})
export class SharedAuthStateService {
  // ============================================================================
  // Core Auth Signals (Private Writable)
  // ============================================================================

  /**
   * Whether the user is currently authenticated.
   * Updated by AuthService on login/logout/token refresh.
   */
  private readonly _isAuthenticated = signal<boolean>(false);

  /**
   * The current user's ID, or null if not authenticated.
   * Updated by AuthService when user info changes.
   */
  private readonly _currentUserId = signal<string | null>(null);

  /**
   * The current tenant ID from the authenticated user's context.
   * This is the user's default/primary tenant, not the active switched tenant.
   * Updated by AuthService when user info changes.
   */
  private readonly _currentTenantId = signal<string | null>(null);

  // ============================================================================
  // Public Readonly Signals (for consumers)
  // ============================================================================

  /**
   * Read-only signal indicating authentication status.
   * Use this in computed signals and templates.
   */
  readonly isAuthenticated = this._isAuthenticated.asReadonly();

  /**
   * Read-only signal containing current user ID.
   * Use this in computed signals and templates.
   */
  readonly currentUserId = this._currentUserId.asReadonly();

  /**
   * Read-only signal containing current tenant ID from user context.
   * Note: This is the user's primary tenant, not the actively selected tenant.
   * For the active tenant, use TenantStore.currentTenant().
   */
  readonly currentTenantId = this._currentTenantId.asReadonly();

  // ============================================================================
  // Computed Signals (derived state)
  // ============================================================================

  /**
   * Alias for isAuthenticated for compatibility with AuthStore.isLoggedIn()
   */
  readonly isLoggedIn = computed(() => this._isAuthenticated());

  /**
   * Whether a user ID is currently set
   */
  readonly hasUser = computed(() => this._currentUserId() !== null);

  /**
   * Whether a tenant ID is currently set
   */
  readonly hasTenant = computed(() => this._currentTenantId() !== null);

  // ============================================================================
  // Write Methods (for AuthService)
  // ============================================================================

  /**
   * Update the authentication status.
   * Called by AuthService on login success, logout, or token refresh.
   *
   * @param value - true if authenticated, false otherwise
   */
  setAuthenticated(value: boolean): void {
    this._isAuthenticated.set(value);
  }

  /**
   * Update the current user ID.
   * Called by AuthService when user data is loaded or cleared.
   *
   * @param userId - The user's ID, or null on logout
   */
  setCurrentUserId(userId: string | null): void {
    this._currentUserId.set(userId);
  }

  /**
   * Update the current tenant ID from user context.
   * Called by AuthService when user data is loaded or cleared.
   *
   * @param tenantId - The user's primary tenant ID, or null on logout
   */
  setCurrentTenantId(tenantId: string | null): void {
    this._currentTenantId.set(tenantId);
  }

  /**
   * Update all auth state at once.
   * Useful when handling login response.
   *
   * @param state - Object containing all auth state values
   */
  setState(state: {
    isAuthenticated: boolean;
    userId: string | null;
    tenantId: string | null;
  }): void {
    this._isAuthenticated.set(state.isAuthenticated);
    this._currentUserId.set(state.userId);
    this._currentTenantId.set(state.tenantId);
  }

  /**
   * Clear all authentication state.
   * Called by AuthService on logout.
   */
  clearState(): void {
    this._isAuthenticated.set(false);
    this._currentUserId.set(null);
    this._currentTenantId.set(null);
  }
}
